import { map } from 'rxjs';
import { CombinedGraphQLErrors } from '@apollo/client/errors';

// apps/vendor apollo client.
//
// Identical in shape to apps/main/src/apollo-client.ts — the vendor app
// talks to the same AppSync API and the token is issued from the vendor
// tenant's own Cognito user pool under the `clearpath-vendor` audience.
// Resolver-level authorization narrows the surface (e.g. only federated
// step_runs owned by this vendor tenant are returned).
import { ApolloClient, ApolloLink, HttpLink, InMemoryCache, Observable } from "@apollo/client";
import { getMainDefinition, relayStylePagination } from "@apollo/client/utilities";
import { setContext } from "@apollo/client/link/context";
import { ErrorLink } from "@apollo/client/link/error";
import { AUTH_TYPE } from "aws-appsync-auth-link";
import { createSubscriptionHandshakeLink } from "aws-appsync-subscription-link";
import { normalizeGraphQLResultErrors } from "@clearpath/shared-types/errors";
import { getAccessToken } from "@clearpath/auth-client";

const appSyncHttpUrl = import.meta.env.VITE_APPSYNC_HTTP_URL as string;
const region = import.meta.env.VITE_COGNITO_REGION as string;

const httpLink = new HttpLink({ uri: appSyncHttpUrl });

const authLink = setContext(async (_operation, { headers }) => {
  const token = await getAccessToken();
  return {
    headers: {
      ...headers,
      Authorization: token ? `Bearer ${token}` : "",
    },
  };
});

const errorLink = new ErrorLink(({ error }) => {
  const graphQLErrors = CombinedGraphQLErrors.is(error) ? error.errors : undefined;
  const networkError = CombinedGraphQLErrors.is(error) ? undefined : error;
  if (graphQLErrors) {
    for (const err of graphQLErrors) {
      if (err.extensions?.code === "UNAUTHENTICATED") {
        // Do NOT hard-tear-down the session here. AppSync returns
        // UNAUTHENTICATED both for a genuinely dead session AND for a
        // transient missing/not-yet-refreshed token during bootstrap — and
        // tearing down on the transient case created a sign-in loop
        // (2026-07-09: callback → "/" → Me 401 → teardown → bounce to login →
        // SSO redirects back → stuck at "Finishing sign-in…"). The auth-client
        // is the single source of truth for definitive teardown (its cold-load
        // /me probe tears down only on `error: "invalid_token"`). Just nudge a
        // token refresh; if the session is truly dead the client tears it down.
        void getAccessToken();
      }
      console.error("[GraphQL]", err.message, err.path);
    }
  }
  if (networkError) {
    console.error("[Network]", networkError.message);
  }
});

// AppSync's realtime WebSocket protocol is AWS-specific (auth + host in
// base64-encoded `header`/`payload` query params, AWS `graphql-ws` subprotocol,
// custom message frames). The generic `graphql-ws` library speaks the standard
// graphql-transport-ws protocol, which AppSync rejects at the upgrade (socket
// closes 1006) — so the old wsLink never connected and the federation inbox
// silently fell back to polling. We use the AWS-published Apollo link (parity
// with apps/main) so the handshake is spoken correctly, and gate `jwtToken` on
// a REAL token — throwing (never returning "") when none is ready — so the WS is
// never opened with a blank Authorization (which AppSync 401s). `jwtToken` is
// re-evaluated per connect/reconnect, so silent token refresh is picked up
// without manual teardown.
const auth = {
  type: AUTH_TYPE.OPENID_CONNECT as const,
  jwtToken: async () => {
    const token = await getAccessToken();
    if (!token) {
      throw new Error("[ws] no access token yet — deferring AppSync realtime connect");
    }
    return token;
  },
};

// createSubscriptionHandshakeLink routes subscriptions over the AppSync realtime
// WS (using `auth`) and delegates all other operations to the HTTP chain — so
// the HTTP link (Bearer auth) is unchanged; only the WS transport switches.
// ENG-655 — `GraphQLError.extensions` does not survive AppSync (the Lambda
// runtime drops own properties on a thrown error; AppSync then reports
// `errorType: "Lambda:Unhandled"`). The api Lambda appends a `#cp-err/1#`
// envelope to `message`; this link decodes it back into `extensions`. WITHOUT
// IT, every `extensions?.code === "..."` branch in this app is dead code.
//
// Links run outside-in for requests and INSIDE-OUT for responses, so it sits as
// close to the terminating HTTP link as possible — it normalizes before
// `errorLink` and before any component reads the error. Inert when no envelope
// is present.
//
// All decoding logic lives in ONE place:
// packages/shared-types/src/graphql-error-envelope.ts (which also documents why
// only the 3-line ApolloLink construction, not the logic, is per-app).
const errorEnvelopeLink = new ApolloLink((operation, forward) =>
  forward(operation).pipe(map((result) => normalizeGraphQLResultErrors(result))),
);

const httpChain = authLink.concat(errorLink).concat(errorEnvelopeLink).concat(httpLink);
// LOCAL-STACK ESCAPE HATCH: the zero-AWS local backend has no AppSync realtime
// endpoint, so createSubscriptionHandshakeLink would loop on a failed WS
// handshake and flood the console. When VITE_DISABLE_SUBSCRIPTIONS is set
// (local-stack only — never in the deployed env config), route subscription
// operations to an inert link that never emits/errors; queries/mutations use
// the HTTP chain and behave identically to prod. Prod is unchanged when unset.
const subscriptionsDisabled =
  (import.meta.env.VITE_DISABLE_SUBSCRIPTIONS as string | undefined) === "true";

const link = subscriptionsDisabled
  ? ApolloLink.split(
      (operation) => {
        const def = getMainDefinition(operation.query);
        return def.kind === "OperationDefinition" && def.operation === "subscription";
      },
      new ApolloLink(() => new Observable(() => {})),
      httpChain,
    )
  : createSubscriptionHandshakeLink({ url: appSyncHttpUrl, region, auth }, httpChain);

export const apolloClient = new ApolloClient({
  link,

  cache: new InMemoryCache({
    typePolicies: {
      Order: { keyFields: ["id"] },
      OrderProduct: { keyFields: ["id"] },
      User: { keyFields: ["id"] },
      FederationLink: { keyFields: ["id"] },
      FederationMessage: { keyFields: ["id"] },
      StepRun: { keyFields: ["id"] },
      File: { keyFields: ["id"] },
      Comment: { keyFields: ["id"] },
      // The federation inbox is an infinite-scroll list keyed by filter.
      // Pagination (`page.after`) intentionally stays out of the cache key.
      Query: {
        fields: {
          orders: relayStylePagination(["filter", "sort"]),
        },
      },
    },
  }),

});
